import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { AHSeparator, Box, TopTab, TopTabItem } from "arkh";

import { FormattedMessage } from "react-intl";

import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { UserAdminMainQuery } from "./__generated__/UserAdminMainQuery.graphql";

import { SafeAreaProvider } from "react-native-safe-area-context";
import {
  StoreAdminNavigationProp,
  StoreAdminNavigationScreenProps,
} from "../StoreAdmin/StoreAdminNavigation";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useKeyboard } from "@/src/OS/useKeyboard";
import { UserAdminGroupDevices } from "./UserAdminGroupDevices";
import { UserAdminEmployees } from "./UserAdminEmployees";
import { UserAdminAccounts } from "./UserAdminAccounts";

import { UserAdminMainComponentEmployeeCreateMutation } from "./__generated__/UserAdminMainComponentEmployeeCreateMutation.graphql";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { useIsFocused } from "@react-navigation/native";
import { ModalTabletAwareKeyboardProvider } from "@/src/components/mobile/ModalTabletAwareKeyboardStickyView";
import { useCreateSharedDeviceAuthRequest } from "./useCreateSharedDeviceAuthRequest";

export const UserAdminMain = (
  props: StoreAdminNavigationScreenProps<"UserAdmin">
) => {
  return (
    <ModalTabletAwareKeyboardProvider>
      <SafeAreaProvider>
        <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
          {(fk) => (
            <UserAdminMainComponent
              navigation={props.navigation}
              fetchKey={fk}
              storeId={props.route.params.storeId}
            />
          )}
        </LazyQueryNetworkBoundrary>
      </SafeAreaProvider>
    </ModalTabletAwareKeyboardProvider>
  );
};

function UserAdminMainComponent(props: {
  navigation: StoreAdminNavigationProp<"UserAdmin">;
  fetchKey: string;
  storeId?: string;
}) {
  const data = useLazyLoadQuery<UserAdminMainQuery>(
    graphql`
      query UserAdminMainQuery($storeId: ID, $first: Int, $after: String) {
        store(id: $storeId) {
          ...UserAdminAccounts_store
          ...UserAdminGroupDevices_store
          ...UserAdminEmployees_store
          hasEmployeeProvider
        }
      }
    `,
    {
      storeId: props.storeId,
      first: 20,
      after: null,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [createEmployee, createEmployeeLoading] =
    useMutation<UserAdminMainComponentEmployeeCreateMutation>(graphql`
      mutation UserAdminMainComponentEmployeeCreateMutation(
        $storeId: ID
        $input: EmployeeCreateInput!
      ) {
        store(id: $storeId) {
          employeeCreate(input: $input) {
            employee {
              id
            }
          }
        }
      }
    `);
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const [mode, setMode] = useState<"shared_devices" | "employees" | "account">(
    "account"
  );

  const keyboard = useKeyboard();
  const isFocused = useIsFocused();
  const [keyboardVisible, setKeyboardVisible] = useState(
    keyboard.keyboardShown
  );

  if (isFocused && keyboardVisible !== keyboard.keyboardShown) {
    setKeyboardVisible(keyboard.keyboardShown);
  }

  const hideHeader = useMemo(() => {
    if (isCompact && keyboardVisible) {
      if (mode === "account") {
        return true;
      }
      if (mode === "employees") {
        return true;
      }
    }
    return false;
  }, [isCompact, keyboardVisible, mode]);

  const onCreateEmployee = useCallback(() => {
    if (createEmployeeLoading) {
      return;
    }
    createEmployee({
      variables: {
        storeId: props.storeId,
        input: {
          name: "",
        },
      },
      onCompleted: (data) => {
        if (data.store?.employeeCreate?.employee) {
          props.navigation.navigateDeprecated("EmployeeEdit", {
            id: data.store.employeeCreate.employee.id,
            storeId: props.storeId,
          });
        }
      },
    });
  }, [props.navigation, props.storeId, createEmployeeLoading, createEmployee]);

  const intl_tokens = useIntlTokens();

  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions({
      ...(mode === "employees" && data.store?.hasEmployeeProvider
        ? getNavigationHeaderOptions({
            type: "BACK",
            title: intl_tokens.UsersAndDevices,
            onBack() {
              props.navigation.goBack();
            },
            more: [
              {
                label: intl_tokens.AddEmployee,
                onPress: onCreateEmployee,
              },
            ],
          })
        : getNavigationHeaderOptions({
            type: "BACK",
            title: intl_tokens.UsersAndDevices,
            onBack() {
              props.navigation.goBack();
            },
          })),
      headerShown: !hideHeader,
      headerShadowVisible: false,
    });
  }, [
    props.navigation,
    hideHeader,
    data.store?.hasEmployeeProvider,
    mode,
    intl_tokens,
    onCreateEmployee,
    createEmployeeLoading,
    getNavigationHeaderOptions,
  ]);

  const showTopTab = !hideHeader;

  const [createAuth, isCreatingAuthRequest] = useCreateSharedDeviceAuthRequest(
    props.storeId
  );
  return (
    <>
      <Box style={{ flex: 1 }} color="surface-background-light">
        {showTopTab ? (
          <>
            <TopTab isCompact={isCompact}>
              <TopTabItem
                selected={mode === "employees"}
                onPress={() => {
                  setMode("employees");
                }}
              >
                {intl_tokens.Employees}
              </TopTabItem>

              <TopTabItem
                selected={mode === "account"}
                onPress={() => {
                  setMode("account");
                }}
              >
                <FormattedMessage
                  defaultMessage="Accounts"
                  description="UserAdmin: TopTab active"
                />
              </TopTabItem>

              <TopTabItem
                selected={mode === "shared_devices"}
                onPress={() => {
                  setMode("shared_devices");
                }}
              >
                <FormattedMessage
                  defaultMessage="Shared Devices"
                  description="UserAdmin: TopTab active"
                />
              </TopTabItem>
            </TopTab>
            <AHSeparator />
          </>
        ) : null}
        {mode === "shared_devices" ? (
          <UserAdminGroupDevices
            store={data.store}
            isCreatingAuthRequest={isCreatingAuthRequest}
            createGroupDevice={() => {
              createAuth().then((res) => {
                props.navigation.navigate("UserAdminCreateGroupDevice", {
                  deviceCode: res.deviceCode,
                  expiresAt: res.expiresAt,
                });
              });
            }}
            openEditGroupDevice={(id: string) => {
              props.navigation.navigateDeprecated("GroupDeviceEdit", {
                id,
                storeId: props.storeId,
              });
            }}
          />
        ) : null}
        {mode === "account" ? (
          <UserAdminAccounts
            store={data.store}
            openAccount={(id: string) => {
              props.navigation.navigateDeprecated("AccountEdit", {
                id,
                storeId: props.storeId,
              });
            }}
            openAccountInvite={(type: "GROUP" | "INDIVIDUAL") => {
              if (type === "INDIVIDUAL") {
                props.navigation.navigate("AccountCreateSelectEmployee", {
                  storeId: props.storeId,
                });
              } else {
                props.navigation.navigateDeprecated("CreateAccount", {
                  type,
                  storeId: props.storeId,
                });
              }
            }}
          />
        ) : null}
        {mode === "employees" ? (
          <UserAdminEmployees
            store={data.store}
            openEmployee={(id: string) => {
              props.navigation.navigateDeprecated("EmployeeEdit", {
                id,
                storeId: props.storeId,
              });
            }}
            createEmployee={onCreateEmployee}
          />
        ) : null}
      </Box>
    </>
  );
}
